import React, { useMemo, useState } from "react";

import AirplaneTicketIcon from "@mui/icons-material/AirplaneTicket";
import { Grid } from "@mui/material";

import Airport from "@/components/Airport";
import BaseAutocomplete from "@/components/BaseAutocomplete";
import { AirportMapItem } from "@/constants/airport";
import { VOTE_PROGRAM_NAME_MAP } from "@/constants/programs";
import useEvent from "@/hooks/useEvent";

interface RouteRequestCondition {
  fromLocation: AirportMapItem[];
  toLocation: AirportMapItem[];
  programs: { code: string; name: string }[];
}

interface RouteRequestProps {
  condition: RouteRequestCondition;
  onChange: (data: Partial<RouteRequestCondition>) => void;
}

const RouteRequest = ({ condition, onChange }: RouteRequestProps) => {
  const [programSearchVal, setProgramSearchVal] = useState("");
  const programList = useMemo(() => {
    const options = [];

    for (const [code, name] of VOTE_PROGRAM_NAME_MAP.entries()) {
      options.push({ name, code });
    }

    return options;
  }, []);

  const handleAirportChange = useEvent(
    ({
      fromLocation,
      toLocation,
    }: {
      fromLocation: AirportMapItem[];
      toLocation: AirportMapItem[];
    }) => {
      onChange({ fromLocation, toLocation });
    }
  );

  const handleProgramChange = useEvent((data: { name: string; code: string }[]) => {
    onChange({ programs: data });
  });

  const programOptions = useMemo(() => {
    const keywords = programSearchVal.trim().toUpperCase();

    if (!programSearchVal.trim()) return programList;

    return programList.filter(
      (ele) =>
        ele.name.toUpperCase().indexOf(keywords) !== -1 ||
        ele.code.toUpperCase().indexOf(keywords) !== -1
    );
  }, [programList, programSearchVal]);

  return (
    <div className="w-full">
      <Grid container alignItems="center" rowGap={2} className="relative">
        <Grid item xs={12}>
          <Airport
            fromLocation={condition.fromLocation}
            toLocation={condition.toLocation}
            onChange={handleAirportChange}
            maxCount={1}
          />
        </Grid>

        <Grid item xs={12}>
          <BaseAutocomplete
            placeholder="Program"
            Icon={<AirplaneTicketIcon />}
            options={programOptions}
            selectData={condition.programs}
            onChange={handleProgramChange}
            maxCount={1}
            onInputChange={(val: string) => setProgramSearchVal(val)}
          />
        </Grid>
      </Grid>
    </div>
  );
};

export default React.memo(RouteRequest);
